import React, { useState, useMemo } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { WBIconButton, WBIcon, WBBox, WBCheckbox } from '@admiin-com/ds-web';
import { LexicalEditor, $getSelection, $isRangeSelection } from 'lexical';
import { $insertNodes, $createTextNode } from 'lexical';
import { $generateNodesFromDOM } from '@lexical/html';
import { useDocumentEditorContext } from '../context/DocumentEditorContext';
import { TemplateType } from '@admiin-com/ds-graphql';

interface HandlebarsControlsProps {
  editor: LexicalEditor;
}

interface PropertyOption {
  label: string;
  value: string;
}

const GROUP_ICONS: Record<string, string> = {
  to: 'Person',
  from: 'Send',
  task: 'Clipboard',
  partner: 'People',
  dates: 'Calendar',
};

const PROPERTY_ICONS: Record<string, string> = {
  name: 'Person',
  firstName: 'Person',
  lastName: 'Person',
  'contact.firstName': 'Person',
  'contact.lastName': 'Person',
  type: 'Pricetag',
  taxNumber: 'DocumentText',
  email: 'Mail',
  'contact.email': 'Mail',
  phone: 'Call',
  'contact.phone': 'Call',
  website: 'Globe',
  brandColor: 'ColorPalette',
  logoUrl: 'Image',
  amount: 'Cash',
  reference: 'Pricetag',
  dueAt: 'Calendar',
  dueCheck: 'AlertCircle',
  notes: 'DocumentText',
  invoiceStatus: 'Document',
  dueTitle: 'DocumentText',
  issuedAt: 'Calendar',
  documentTitle: 'DocumentText',
  signatureRequired: 'Pencil',
  frequencyPeriod: 'Repeat',
  period: 'Calendar',
  settlementStatus: 'Document',
  today: 'Calendar',
  year: 'Calendar',
  month: 'Calendar',
  'address.address1': 'Location',
  'address.address2': 'Location',
  'address.city': 'Location',
  'address.country': 'Location',
  'address.state': 'Location',
  'address.postalCode': 'Location',
};

const getPropertyIcon = (value: string) =>
  PROPERTY_ICONS[value] || 'DocumentText';

const BASE_PROPERTY_GROUPS = {
  to: [
    { label: 'Name', value: 'name' },
    { label: 'First Name', value: 'firstName' },
    { label: 'Last Name', value: 'lastName' },
    { label: 'Type', value: 'type' },
    { label: 'ABN', value: 'taxNumber' },
    { label: 'ACN', value: 'taxNumber' },
    { label: 'Email', value: 'email' },
    { label: 'Phone', value: 'phone' },
    { label: 'Address - Line 1', value: 'address.address1' },
    { label: 'Address - Line 2', value: 'address.address2' },
    { label: 'Address - City', value: 'address.city' },
    { label: 'Address - Country', value: 'address.country' },
    { label: 'Address - State', value: 'address.state' },
    { label: 'Address - Postal Code', value: 'address.postalCode' },
  ],
  from: [
    { label: 'Name', value: 'name' },
    { label: 'First Name', value: 'contact.firstName' },
    { label: 'Last Name', value: 'contact.lastName' },
    { label: 'Type', value: 'type' },
    { label: 'ABN', value: 'taxNumber' },
    { label: 'ACN', value: 'taxNumber' },
    { label: 'Email', value: 'contact.email' },
    { label: 'Phone', value: 'contact.phone' },
    { label: 'Website', value: 'website' },
    { label: 'Brand Color', value: 'brandColor' },
    { label: 'Logo URL', value: 'logoUrl' },
    { label: 'Address - Line 1', value: 'address.address1' },
    { label: 'Address - Line 2', value: 'address.address2' },
    { label: 'Address - City', value: 'address.city' },
    { label: 'Address - Country', value: 'address.country' },
    { label: 'Address - State', value: 'address.state' },
    { label: 'Address - Postal Code', value: 'address.postalCode' },
  ],
  task: [
    { label: 'Amount', value: 'amount' },
    { label: 'Reference', value: 'reference' },
    { label: 'Due Date', value: 'dueAt' },
    { label: 'Due Check', value: 'dueCheck' },
    { label: 'Notes', value: 'notes' },
    { label: 'Invoice Status', value: 'invoiceStatus' },
    { label: 'Due Title', value: 'dueTitle' },
    { label: 'Issued Date', value: 'issuedAt' },
    { label: 'Document Title', value: 'documentTitle' },
    { label: 'Signature Required', value: 'signatureRequired' },
    { label: 'Frequency Period', value: 'frequencyPeriod' },
    { label: 'Period', value: 'period' },
    { label: 'Settlement Status', value: 'settlementStatus' },
    { label: 'Year', value: 'year' },
  ],
  partner: [
    { label: 'First Name', value: 'firstName' },
    { label: 'Last Name', value: 'lastName' },
    { label: 'Email', value: 'email' },
    { label: 'Phone', value: 'phone' },
    { label: 'Signature URL', value: 'signatureUrl' },
  ],
  dates: [
    { label: "Today's Date", value: 'today' },
    { label: 'Current Year', value: 'year' },
    { label: 'Current Month', value: 'month' },
  ],
};

export const HandlebarsControls: React.FC<HandlebarsControlsProps> = ({
  editor,
}) => {
  const [menuAnchors, setMenuAnchors] = useState<
    Record<string, HTMLElement | null>
  >({});
  const { templateType, includeLetterhead, setIncludeLetterhead } =
    useDocumentEditorContext();

  const propertyGroups = useMemo(() => {
    if (templateType === TemplateType.TAX_BINDING) {
      return {
        ...BASE_PROPERTY_GROUPS,
        task: [
          ...BASE_PROPERTY_GROUPS.task,
          { label: 'Each Task', value: 'tasksEach' },
          { label: 'Tasks Table', value: 'tasksTable' },
        ],
      };
    }
    return BASE_PROPERTY_GROUPS;
  }, [templateType]);

  const insertHandlebarsProperty = (group: string, property: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (group === 'task' && property === 'settlementStatus') {
          const startNode = $createTextNode(
            '{{#if (eq task.settlementStatus "Refundable")}}\n'
          );
          const cursorNode = $createTextNode('');
          const endNode = $createTextNode(
            '\n{{/if}}\n{{#if (eq task.settlementStatus "Payable")}}\n\n{{/if}}\n{{#if (eq task.settlementStatus "Nil")}}\n\n{{/if}}'
          );
          $insertNodes([startNode, cursorNode, endNode]);
          cursorNode.select();
        } else if (group === 'task' && property === 'dueCheck') {
          const startNode = $createTextNode(
            '{{#if (or (isPastDate task.dueAt) (isTodayDate task.dueAt))}}\n  '
          );
          const cursorNode = $createTextNode('');
          const endNode = $createTextNode('\n{{else}}\n  \n{{/if}}');
          $insertNodes([startNode, cursorNode, endNode]);
          cursorNode.select();
        } else if (group === 'task' && property === 'tasksEach') {
          const startNode = $createTextNode(
            '{{#each (sortTasksByEntityType tasks)}}\n  {{taskEntityName this}}\n  {{entityType}}\n  {{settlementStatus}}\n  {{taskSettlementAmount this}}\n  {{taskDueDate this}}\n'
          );
          const cursorNode = $createTextNode('');
          const endNode = $createTextNode('{{/each}}');
          $insertNodes([startNode, cursorNode, endNode]);
          cursorNode.select();
        } else if (group === 'task' && property === 'tasksTable') {
          // Create table with handlebars loop inside tbody
          const completeHTML = `<table>
  <thead>
    <tr>
      <th><strong>Entity</strong></th>
      <th><strong>Tax Payable / (Refundable)</strong></th>
      <th><strong>Due Date</strong></th>
    </tr>
  </thead>
  <tbody>
    <tr data-hbs-hidden="true"><td>{{#each (sortTasksByEntityType tasks)}}</td></tr>
    <tr>
      <td>{{taskEntityName this}}</td>
      <td>{{formatTaskSettlementAmount (taskSettlementAmount this) settlementStatus}}</td>
      <td>{{#if (or (isPastDate (taskDueDate this)) (isTodayDate (taskDueDate this)))}}<span style="color:red">ASAP</span>{{else}}{{formatDate (taskDueDate this) "dd/MM/yyyy"}}{{/if}}</td>
    </tr>
    <tr data-hbs-hidden="true"><td>{{/each}}</td></tr>
  </tbody>
</table>`;

          const parser = new DOMParser();
          const dom = parser.parseFromString(completeHTML, 'text/html');
          const nodes = $generateNodesFromDOM(editor, dom);
          $insertNodes(nodes);

          if (nodes.length > 0) {
            nodes[nodes.length - 1].selectNext();
          }
        } else {
          const handlebarsText = `{{${group}.${property}}}`;
          const textNode = $createTextNode(handlebarsText);
          $insertNodes([textNode]);

          // Insert a space after the property to break the formatting
          const spaceNode = $createTextNode(' ');
          $insertNodes([spaceNode]);

          // Move cursor after the space
          spaceNode.selectNext();
        }
      }
    });

    setMenuAnchors((prev) => ({ ...prev, [group]: null }));
  };
  const handleOpenMenu = (group: string, anchor: HTMLElement) => {
    setMenuAnchors((prev) => ({ ...prev, [group]: anchor }));
  };

  const handleCloseMenu = (group: string) => {
    setMenuAnchors((prev) => ({ ...prev, [group]: null }));
  };

  const groupLabels: Record<string, string> = {
    to: 'To',
    from: 'From',
    partner: 'Partner',
    task: 'Task',
    dates: 'Dates',
  };

  return (
    <>
      {Object.keys(propertyGroups).map((group) => (
        <React.Fragment key={group}>
          <WBIconButton
            size="small"
            onMouseDown={(e) => {
              e.preventDefault();
              handleOpenMenu(group, e.currentTarget);
            }}
            title={`Insert ${groupLabels[group]} Placeholder`}
            sx={{
              minWidth: 90,
              justifyContent: 'flex-start',
              color: 'text.primary',
              gap: '6px',
              marginRight: 1,
            }}
          >
            <WBIcon
              name={GROUP_ICONS[group]}
              library="ioniconOutline"
              size="small"
              color="text.primary"
            />
            {groupLabels[group]}
          </WBIconButton>

          <Menu
            anchorEl={menuAnchors[group]}
            open={Boolean(menuAnchors[group])}
            onClose={() => handleCloseMenu(group)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{
              sx: {
                minWidth: 200,
                '& .MuiMenuItem-root': {
                  fontSize: '12px',
                  minHeight: 30,
                  padding: '6px 12px',
                },
              },
            }}
          >
            {(propertyGroups[group as keyof typeof propertyGroups] || []).map(
              (property) => (
                <MenuItem
                  key={property.value}
                  onClick={() =>
                    insertHandlebarsProperty(group, property.value)
                  }
                >
                  <WBIcon
                    name={getPropertyIcon(property.value)}
                    library="ioniconOutline"
                    size={2.5}
                  />

                  <WBBox component="span" ml={1}>
                    {property.label}
                  </WBBox>
                  <WBBox
                    component="span"
                    fontFamily="monospace"
                    color="text.secondary"
                    ml={1}
                    fontSize={10}
                  >
                    {`{{${group}.${property.value}}}`}
                  </WBBox>
                </MenuItem>
              )
            )}
          </Menu>
        </React.Fragment>
      ))}
      {templateType !== TemplateType.LETTERHEAD && (
        <WBCheckbox
          size="small"
          label="Include letterhead"
          checked={includeLetterhead}
          onChange={(e) => setIncludeLetterhead(e.target.checked)}
          sx={{
            '.MuiCheckbox-root': { padding: 0 },
          }}
        />
      )}
    </>
  );
};
